import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import {
  analyticsCreateRide,
  analyticsCreateRideSeries,
  analyticsEditedRide,
  analyticsEditedRideSeries,
  analyticsHitRideCap,
  analyticsRemoveRider,
  analyticsSubmittedRideRequest,
} from '../api/services/Analytics';
import {
  addRideSeries,
  addStandaloneRide,
  updatePartialRideSeries,
  updatePartialStandaloneRide,
} from '../api/services/Rides';
import { RideSeriesResponse } from '../api/types/RidesTypes';
import { RIDE_CANCEL_TYPES } from '../constants';
import { useRideEditor } from '../rides/hooks/useRideEditor';
import { useNavigation } from '../shared/hooks/useNavigation';
import { useLoaderStore } from '../shared/stores/loaderStore';
import { useToastStore } from '../shared/stores/toastStore';
import { leaveBreadcrumb, reportError } from '../utils/errorReporting/error';
import { trackFullStoryEvent } from '../vendor/features/FullStory/FullStory.service';

import { useSmsConfirmation } from './useSmsConfirmation';
import { useTimezone } from './useTimezone';

type WarningModal = {
  isOpen: boolean;
  canResubmit?: boolean | undefined;
  message: string;
};

type ActionTypeEnum = 'save-ride' | 'duplicate-ride' | 'reverse-duplicate-ride';

type CallbackFunction<T> = (action: T, cb: void | (() => void)) => void;
interface UseRideSubmitHooks {
  formData: Partial<RideFormData> | null;
  setFormData: Dispatch<SetStateAction<Partial<RideFormData> | null>>;
  warningModal: WarningModal;
  rideCapModalOpen: boolean;
  setRideCapModalOpen: Dispatch<SetStateAction<boolean>>;
  riderServiceRequestModalOpen: boolean;
  setRiderServiceRequestModalOpen: Dispatch<SetStateAction<boolean>>;
  riderListOfServices: Array<string>;
  setRiderListOfServices: Dispatch<SetStateAction<Array<string>>>;
  setWarningModal: Dispatch<SetStateAction<WarningModal>>;
  saveRide: (rideData) => void;
  editRide: (rideId, rideData, anchorTime, hasSmartPoolingSuggestions, onSuccessCallback?) => void;
  setActionType: CallbackFunction<ActionTypeEnum>;
  handleOnSuccessAction: (rideSlug: string, nextActionType: ActionTypeEnum, showToast?: boolean) => void;
  setReturnInfo: (returnInfo: { riderName: string; riderSlug: string }) => void;
}

export const useRideSubmit = (isSeries: boolean, estimatedFare: string | undefined = undefined): UseRideSubmitHooks => {
  const { navigateTo } = useNavigation();
  const [formData, setFormData] = useState<Partial<RideFormData> | null>(null);
  const [warningModal, setWarningModal] = useState<WarningModal>({
    isOpen: false,
    message: '',
  });
  const [rideCapModalOpen, setRideCapModalOpen] = useState(false);
  const [riderServiceRequestModalOpen, setRiderServiceRequestModalOpen] = useState(false);
  const [riderListOfServices, setRiderListOfServices] = useState<string[]>([]);
  const [actionType, setActionType] = useStateCallback<ActionTypeEnum>('save-ride');
  const [returnInfo, setReturnInfo] = useState({ riderName: '', riderSlug: '' });
  const { addToast, resetToasts } = useToastStore();
  const { showLoader, hideLoader } = useLoaderStore();
  const { mergeDateTime } = useTimezone();
  const { handleShowSmsConfirmationToast } = useSmsConfirmation();
  const { getRiderOptions } = useRideEditor();
  const { ridersOnRide } = getRiderOptions();

  const handleOnSuccessAction = (rideSlug, nextActionType, showToast) => {
    if (rideSlug) {
      switch (nextActionType) {
        case 'duplicate-ride':
          navigateTo('/rides/duplicate', { id: rideSlug, isSeries: isSeries, showToast: showToast });
          break;
        case 'reverse-duplicate-ride':
          navigateTo('/rides/duplicate', { id: rideSlug, isReverse: true, isSeries: isSeries, showToast: showToast });
          break;
        default:
          navigateTo(`/rides/${rideSlug}`, { riderName: returnInfo?.riderName, riderSlug: returnInfo?.riderSlug });
          break;
      }
    }
  };

  const hasCalendarConflict = (errorMessages: string[], responseStatus?: number) => {
    // use first message if response status is 409

    const errorMessage =
      errorMessages.find((error: string) => responseStatus === 409 || error.indexOf('HopSkipDrive is closed') !== -1) ||
      '';

    if (errorMessage) {
      // 409 status code means this conflict allow for re-submitting the same payload
      // bypassing the calendar conflitct
      setWarningModal({ isOpen: true, canResubmit: responseStatus === 409, message: errorMessage });

      return true;
    }

    return false;
  };

  const saveRide = async (rideData) => {
    const allServices = ridersOnRide
      .flatMap((r) => r.services?.map((service) => service.code))
      .filter((code): code is string => typeof code === 'string');

    const { time, anchorTime, tripTemplateAttributes, calendarSlug, ...rest } = rideData;

    const formattedAnchorTime = mergeDateTime(tripTemplateAttributes?.startDate || anchorTime, time);

    const rideFormattedData = {
      ...(isSeries
        ? {
            tripTemplateAttributes: {
              ...tripTemplateAttributes,
              startDate: formattedAnchorTime,
              endDate: mergeDateTime(tripTemplateAttributes?.endDate || anchorTime, '23:59:59'),
            },
          }
        : { anchorTime: formattedAnchorTime }),
      ...(calendarSlug && { calendarSlug: calendarSlug }),
      ...rest,
    };

    showLoader('Saving');

    const newRideResponse = isSeries
      ? await addRideSeries(rideFormattedData)
      : await addStandaloneRide(rideFormattedData);

    hideLoader();
    if (newRideResponse?.type === 'error') {
      if (newRideResponse.error.conflictType?.includes('ride_cap')) {
        setRideCapModalOpen(true);
        setFormData(rideData);
        isSeries && analyticsHitRideCap(rideData, estimatedFare || '');

        return;
      }

      if (newRideResponse.error.conflictType?.includes('requestable_services')) {
        setRiderServiceRequestModalOpen(true);
        setRiderListOfServices(newRideResponse.data?.services);
        setFormData(rideData);

        return;
      }

      if (!newRideResponse.status || newRideResponse.status === 504) {
        flushSync(() => resetToasts());
        addToast({
          severity: 'error',
          message: `The create is taking longer than usual. Please wait a few minutes to check and see if the ${
            isSeries ? 'ride series were' : 'ride was'
          } created.`,
        });
        const failure = newRideResponse.status === 504 ? '504 error' : 'request without HTTP status';

        leaveBreadcrumb(`Ride creation details from ${failure}`, rideFormattedData, 'error');
        reportError(
          new RideCreationError(
            newRideResponse.status === 504
              ? '504 error when creating ride'
              : 'Request failed without HTTP status when creating ride'
          )
        );

        return;
      }
      if (!isSeries && hasCalendarConflict(newRideResponse.error.errors, newRideResponse.status)) {
        setFormData(rideData);

        return;
      }
      flushSync(() => resetToasts());
      addToast({
        severity: 'error',
        message: (
          <>
            <p>{'Oops! We were unable to create this trip due to the following issues:'}</p>
            {newRideResponse.error.errors.map((message, index) => (
              <p key={`error-message-${index}`}>{`• ${message}`}</p>
            ))}
            <p>{'Please make the appropriate changes and try saving again.'}</p>
          </>
        ),
      });
    } else {
      isSeries && isRideSeriesResponse(newRideResponse.data)
        ? analyticsCreateRideSeries(newRideResponse.data.templateSlug || '', rideData, allServices)
        : analyticsCreateRide(newRideResponse.data.slug, rideData, allServices);

      trackFullStoryEvent('ride_create_completed', {
        ride_type: rideData.timeAnchor,
        is_recurring: isSeries,
        has_special_services: allServices.length > 0,
      });

      if (rideData.as_request && isSeries && isRideSeriesResponse(newRideResponse.data)) {
        analyticsSubmittedRideRequest(newRideResponse.data.templateSlug, rideData, estimatedFare || '');
      }

      handleOnSuccessAction(newRideResponse.data.slug, actionType, true);
      actionType === 'save-ride' && !rideData.as_request && handleShowSmsConfirmationToast(isSeries, 'created');
    }
  };

  const editRide = async (
    rideId: string,
    rideData: Partial<RideFormData> & { updateFuture?: string; removedRiders?: string[]; status?: string },
    anchorTime: string,
    hasSmartPoolingSuggestions: boolean,
    onSuccessCallback?: () => Promise<void>
  ) => {
    const { calendarSlug, time, waypointsAttributes, removedRiders, status, ...data } = rideData;
    const fieldsChanged = Object.keys(rideData).filter(
      (key) => !['updateFuture', 'removedRiders', 'status'].includes(key)
    );
    const allServices = ridersOnRide
      .flatMap((r) => r.services?.map((service) => service.code))
      .filter((code): code is string => typeof code === 'string');

    const updatedRide = {
      ...data,
      ...(calendarSlug && { calendarSlug: calendarSlug }),

      ...(waypointsAttributes && {
        waypointsAttributes: waypointsAttributes?.map((wp) => {
          const { passengerIds, ...rest } = wp;

          // Needs to clear the riders on the waypoint, otherwise they get added/duplicated to the ride
          return { ...rest, passengerIds: wp.anchor ? [] : wp.passengerIds };
        }),
      }),
    };

    showLoader('Saving');

    const response = isSeries
      ? await updatePartialRideSeries(rideId, updatedRide)
      : await updatePartialStandaloneRide(rideId, updatedRide);

    hideLoader();
    if (response?.type === 'error') {
      if (!response.status || response.status === 504) {
        flushSync(() => resetToasts());
        addToast({
          severity: 'error',
          message: `The update is taking longer than usual. Please wait a few minutes to check and see if the ${
            isSeries ? 'ride series were' : 'ride was'
          } updated.`,
        });
        const failure = response.status === 504 ? '504 error' : 'request without HTTP status';

        leaveBreadcrumb(`Ride update details from ${failure}`, updatedRide, 'error');
        reportError(
          new RideUpdateError(
            response.status === 504
              ? '504 error when updating ride'
              : 'Request failed without HTTP status when updating ride'
          )
        );

        return;
      }
      if (
        !isSeries &&
        !response.error.conflictType?.includes('ride_cap') &&
        hasCalendarConflict(response.error.errors, response.status)
      ) {
        return;
      }
      flushSync(() => resetToasts());
      addToast({
        severity: 'error',
        message: (
          <>
            <p>{'Oops! We were unable to update this trip due to the following issues:'}</p>
            {response.error.errors.map((message, index) => (
              <p key={`update-error-message-${index}`}>{`• ${message}`}</p>
            ))}
            <p>{'Please make the appropriate changes and try saving again.'}</p>
          </>
        ),
      });
    } else {
      onSuccessCallback && (await onSuccessCallback());
      hideLoader();
      isSeries && isRideSeriesResponse(response.data)
        ? analyticsEditedRideSeries(
            response.data.templateSlug,
            updatedRide,
            hasSmartPoolingSuggestions,
            allServices,
            formData?.timeAnchor
          )
        : analyticsEditedRide(response.data.slug, anchorTime, allServices);

      trackFullStoryEvent('ride_edited', { fields_changed: fieldsChanged });

      if (removedRiders?.length) {
        removedRiders.forEach((riderSlug) => analyticsRemoveRider(riderSlug, rideId, status));
      }
      handleOnSuccessAction(response.data.slug, actionType, true);
      actionType === 'save-ride' &&
        handleShowSmsConfirmationToast(isSeries && rideData.updateFuture !== RIDE_CANCEL_TYPES.singleRide, 'updated');
    }
  };

  return {
    formData,
    setFormData,
    warningModal,
    setWarningModal,
    saveRide,
    editRide,
    setActionType,
    handleOnSuccessAction,
    setReturnInfo,
    rideCapModalOpen,
    setRideCapModalOpen,
    riderServiceRequestModalOpen,
    setRiderServiceRequestModalOpen,
    riderListOfServices,
    setRiderListOfServices,
  };
};

type CallbackFunctionRef<T> = (state: { action: T; shouldCall: boolean }) => void;

const useStateCallback = <T extends ActionTypeEnum>(initialState: T): [T, CallbackFunction<T>] => {
  const [state, setState] = useState({ action: initialState, shouldCall: false });
  const callbackFunctionRef = useRef<CallbackFunctionRef<T> | null>(null);

  const setStateCallback = (newState, newCallbackFunction) => {
    callbackFunctionRef.current = newCallbackFunction;
    setState({ action: newState, shouldCall: true });
  };

  useEffect(() => {
    if (state.shouldCall && callbackFunctionRef.current) {
      callbackFunctionRef.current(state);
      callbackFunctionRef.current = null;
      setState((prevState) => ({ ...prevState, shouldCall: false }));
    }
  }, [state]);

  return [state.action, setStateCallback];
};

class RideCreationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'RideCreationError';
  }
}

class RideUpdateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RideUpdateError';
  }
}

function isRideSeriesResponse(response): response is RideSeriesResponse {
  return 'templateSlug' in response;
}
